/**
 * v1.8 Cluster B.5 (D-146) — Privatbereich: der persönliche, Workspace-
 * ÜBERGREIFENDE Überblick eines Nutzers, GETRENNT vom einzelnen Workspace
 * (Mandant). Speist sich aus `GET /api/private/overview` (eigene Aufgaben,
 * Termine, Zeitblöcke, Tag-Typen + die Liste der eigenen Workspaces). Reiner
 * Host-Layer (D-04).
 *
 * Hinweis: Das Backend (B.5) liefert die Aggregation; die Feinausgestaltung
 * dieser Seite gegen die WS-Mini-Spec/Mockups steht noch aus — vorerst ein
 * schlichter, datengetriebener Überblick in den bestehenden Layout-Primitiven.
 */

import { useCallback, useEffect, useState } from "react";

import { ryfeApi } from "../api";
import { useRyfe } from "../RyfeContext";
import { navigate } from "../router";

import "./ryfe.scss";

import type {
  PrivateOverview,
  PrivateSystemlists,
  RyfeWorkspace,
} from "../api";

const fmtDate = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString() : "—";

const taskTitle = (t: { title?: string | null; name?: string | null }) =>
  (t.title || t.name || "").toString().trim() || "(ohne Titel)";

export const RyfePrivate = () => {
  const { workspaces, setWorkspace } = useRyfe();
  const [data, setData] = useState<PrivateOverview | null>(null);
  const [sys, setSys] = useState<PrivateSystemlists | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await ryfeApi.privateOverview());
    } catch (e) {
      setError((e as Error).message);
    }
    // D-146 — die Cross-WS-Systemlisten-Aggregation; optional: schlägt sie fehl,
    // bleibt der Überblick leer, der Rest der Seite funktioniert weiter.
    try {
      setSys(await ryfeApi.privateSystemlists());
    } catch {
      /* aggregation optional */
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Workspace-Namen für die Aufgaben-Herkunft (aus der WS-Liste auflösen).
  const wsName = (id?: string | null) =>
    (data?.workspaces || workspaces).find((w) => w.id === id)?.name ?? "—";

  const openWorkspace = (ws: RyfeWorkspace) => {
    setWorkspace(ws);
    navigate("/files");
  };

  const openTasks = (data?.tasks || []).filter(
    (t) => (t.status || "").toString() !== "erledigt",
  );

  return (
    <div className="ryfe-page">
      <h1 className="ryfe-page__title">Privatbereich</h1>
      <p className="ryfe-muted" style={{ padding: 0 }}>
        Dein persönlicher, Workspace-übergreifender Überblick — unabhängig vom
        einzelnen Workspace.
      </p>

      {error && <div className="ryfe-notice ryfe-notice--warn">{error}</div>}

      {/* Meine Workspaces (Mandanten) — Wechsel per Klick. */}
      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">Meine Workspaces</h2>
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Rolle</th>
              </tr>
            </thead>
            <tbody>
              {(data?.workspaces || workspaces).map((ws) => (
                <tr key={ws.id} className="ryfe-row">
                  <td>
                    <button
                      className="ryfe-linkish"
                      onClick={() => openWorkspace(ws)}
                    >
                      {ws.name}
                    </button>
                  </td>
                  <td>{ws.role}</td>
                </tr>
              ))}
              {(data?.workspaces || workspaces).length === 0 && (
                <tr>
                  <td colSpan={2} className="ryfe-muted">
                    Noch keine Workspaces.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Meine offenen Aufgaben über ALLE Workspaces. */}
      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">
          Meine offenen Aufgaben ({openTasks.length})
        </h2>
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>Aufgabe</th>
                <th>Status</th>
                <th>Fällig</th>
                <th>Workspace</th>
              </tr>
            </thead>
            <tbody>
              {openTasks.map((t) => (
                <tr key={t.id} className="ryfe-row">
                  <td>{taskTitle(t)}</td>
                  <td>{t.status ?? "—"}</td>
                  <td>{fmtDate(t.due_date)}</td>
                  <td>{wsName(t.workspace_id)}</td>
                </tr>
              ))}
              {openTasks.length === 0 && (
                <tr>
                  <td colSpan={4} className="ryfe-muted">
                    Keine offenen Aufgaben.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Aggregierte Systemlisten über ALLE Workspaces (D-146). */}
      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">Systemlisten — aggregiert</h2>
        <p className="ryfe-muted" style={{ padding: 0 }}>
          Über alle deine Workspaces zusammengefasst.
        </p>
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>Systemliste</th>
                <th>Anzahl</th>
              </tr>
            </thead>
            <tbody>
              <tr className="ryfe-row">
                <td>Aufgaben</td>
                <td>{sys?.entities.tasks.count ?? 0}</td>
              </tr>
              <tr className="ryfe-row">
                <td>Projekte</td>
                <td>{sys?.entities.projects.count ?? 0}</td>
              </tr>
              <tr className="ryfe-row">
                <td>Kontakte</td>
                <td>{sys?.entities.contacts.count ?? 0}</td>
              </tr>
              <tr className="ryfe-row">
                <td>Lesezeichen</td>
                <td>{sys?.entities.bookmarks.count ?? 0}</td>
              </tr>
              <tr className="ryfe-row">
                <td>Termine</td>
                <td>{sys?.entities.appointments.count ?? 0}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Projekte — Top-N cross-WS mit Herkunft. */}
      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">
          Projekte ({sys?.entities.projects.count ?? 0})
        </h2>
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Workspace</th>
              </tr>
            </thead>
            <tbody>
              {(sys?.entities.projects.items || []).map((p) => (
                <tr key={p.id} className="ryfe-row">
                  <td>{p.name ?? "(ohne Titel)"}</td>
                  <td>{p.status ?? "—"}</td>
                  <td>{p.workspace_name}</td>
                </tr>
              ))}
              {(sys?.entities.projects.items || []).length === 0 && (
                <tr>
                  <td colSpan={3} className="ryfe-muted">
                    Keine Projekte.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Kontakte — Top-N cross-WS mit Herkunft. */}
      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">
          Kontakte ({sys?.entities.contacts.count ?? 0})
        </h2>
        <div className="ryfe-table-wrap">
          <table className="ryfe-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Typ</th>
                <th>Workspace</th>
              </tr>
            </thead>
            <tbody>
              {(sys?.entities.contacts.items || []).map((c) => (
                <tr key={c.id} className="ryfe-row">
                  <td>{c.display_name ?? "(ohne Namen)"}</td>
                  <td>{c.kind ?? "—"}</td>
                  <td>{c.workspace_name}</td>
                </tr>
              ))}
              {(sys?.entities.contacts.items || []).length === 0 && (
                <tr>
                  <td colSpan={3} className="ryfe-muted">
                    Keine Kontakte.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Termine (Kurzüberblick). */}
      <div className="ryfe-panel">
        <h2 className="ryfe-panel__title">
          Termine ({(data?.appointments || []).length})
        </h2>
        <p className="ryfe-muted" style={{ padding: 0 }}>
          Cross-Workspace-Termine aus deinem Kalender.
        </p>
      </div>
    </div>
  );
};
